import React, { useEffect, useState } from 'react';
import {
    getSoniaScoreboardService,
    askSoniaScoreboardService,
    addSoniaScoreboardActionService,
    doneSoniaScoreboardActionService,
} from '../../../../Services/CRMAdminServices/CRMOperator';

/*
  SONIA'S SCOREBOARD, ON THE DASHBOARD.

  Richard, 2 Oct 2026: the scoreboard belongs on Dashboard MB and Dashboard Client.

  Every number here is produced in one place - the SMS engine - and read, not
  recalculated. Two sources feed it and they are different in kind, so the panel keeps
  them apart rather than blending them into one row of figures:

    what she did    counted from her own database: records, messages, replies,
                    appointments booked, opt-outs, hours given back.
    what it made    arithmetic on three numbers only a human can know - appointments
                    attended, loans written, settled lending. Until the broker enters
                    them this half is honestly blank. A projected return on a page that
                    looks like a record is how a tool starts lying.

  Under those two rows is the coaching, which is what makes this a session rather than a
  report. Richard, 2 Oct 2026: "Sonia coaches clients inside MB gameplans and coaches MB
  on their business." This is the second half of that - she is looking at exactly this
  page, the same counts and the same roadblocks, and what gets agreed is written down with
  an owner against it. An agreed action stays here every month until somebody ticks it,
  so the awkward one cannot quietly fall off.

  Unreachable or not configured, it says so in one line and the rest of the dashboard
  carries on. A scoreboard is never worth taking a dashboard down for.
*/

const INK = '#10214A', MUTED = '#4A5567', LINE = '#DCDFE4';
const DISPLAY = '"Archivo", "Helvetica Neue", Helvetica, Arial, sans-serif';
const card: React.CSSProperties = { background: '#fff', border: `1px solid ${LINE}`, borderRadius: 6, padding: '14px 16px' };
const h2: React.CSSProperties = {
    fontFamily: DISPLAY, fontSize: 12, fontWeight: 700, letterSpacing: '.11em', textTransform: 'uppercase',
    color: MUTED, margin: '0 0 10px', paddingBottom: 6, borderBottom: `2px solid ${LINE}`,
};
const figure: React.CSSProperties = { fontFamily: DISPLAY, fontVariantNumeric: 'tabular-nums', letterSpacing: '-.02em' };

const num = (v: any) => Number(v ?? 0).toLocaleString();
const money = (v: any) => `$${Math.round(Number(v ?? 0)).toLocaleString()}`;

const Cell: React.FC<{ label: string; value: string; note?: string; dim?: boolean }> = ({ label, value, note, dim }) => (
    <div style={{ border: `1px solid ${LINE}`, borderRadius: 6, padding: '11px 13px', background: dim ? '#F7F8FA' : '#fff' }}>
        <div style={{ fontSize: 11, color: MUTED, textTransform: 'uppercase', letterSpacing: '.06em' }}>{label}</div>
        <div style={{ ...figure, fontSize: 25, fontWeight: 700, color: dim ? MUTED : INK, lineHeight: 1.2, marginTop: 4 }}>{value}</div>
        {note && <div style={{ fontSize: 11, color: MUTED, marginTop: 3 }}>{note}</div>}
    </div>
);

const grid: React.CSSProperties = { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12 };

type Props = { audience: 'brokers' | 'clients' };

const Scoreboard: React.FC<Props> = ({ audience }) => {
    const [data, setData] = useState<any>(null);
    const [msg, setMsg] = useState<string>('');
    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState<string>('');

    //  ---- the coaching half ----
    //  The actions are held here as well as on the server so a tick disappears the moment
    //  it is clicked. The server is the record; this is only so the page does not feel dead
    //  while it saves.
    const [actions, setActions] = useState<any[]>([]);
    const [owner, setOwner] = useState('You');
    const [title, setTitle] = useState('');
    const [due, setDue] = useState('');
    const [chat, setChat] = useState<{ role: string; content: string }[]>([]);
    const [question, setQuestion] = useState('');
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);

    const add = () => {
        const t = title.trim();
        if (!t || busy) return;
        setBusy(true); setNote('');
        addSoniaScoreboardActionService({ audience, owner, title: t, due: due.trim() })
            .then((r: any) => {
                const d = r?.data;
                if (d?.ok === false) { setNote(d?.message || 'That did not save.'); return; }
                setActions(a => [...a, { id: d?.id, owner, title: t, detail: '', due: due.trim() }]);
                setTitle(''); setDue('');
            })
            .catch(() => setNote('That did not save.'))
            .finally(() => setBusy(false));
    };

    const tick = (id: number) => {
        if (!id || busy) return;
        setBusy(true); setNote('');
        doneSoniaScoreboardActionService(id)
            .then((r: any) => {
                if (r?.data?.ok === false) { setNote(r?.data?.message || 'That did not save.'); return; }
                setActions(a => a.filter(x => x.id !== id));
            })
            .catch(() => setNote('That did not save.'))
            .finally(() => setBusy(false));
    };

    const ask = () => {
        const q = question.trim();
        if (!q || busy) return;
        const history = chat.slice(-8);
        setChat(c => [...c, { role: 'user', content: q }]);
        setQuestion(''); setBusy(true); setNote('');
        askSoniaScoreboardAnswer(q, history);
    };

    const askSoniaScoreboardAnswer = (q: string, history: { role: string; content: string }[]) => {
        askSoniaScoreboardService({ audience, period: period || undefined, message: q, history })
            .then((r: any) => {
                const d = r?.data;
                const answer = d?.answer || d?.message || 'I could not answer that one.';
                setChat(c => [...c, { role: 'assistant', content: answer }]);
            })
            .catch(() => setChat(c => [...c, { role: 'assistant', content: 'I could not reach my notes just then.' }]))
            .finally(() => setBusy(false));
    };

    useEffect(() => {
        let alive = true;
        setLoading(true);
        getSoniaScoreboardService({ audience, period: period || undefined })
            .then((r: any) => {
                if (!alive) return;
                const d = r?.data;
                if (d?.ok === false) { setData(null); setMsg(d?.message || 'Sonia’s scoreboard is not available.'); }
                else { setData(d); setMsg(''); setActions(Array.isArray(d?.next) ? d.next : []); }
            })
            .catch(() => { if (alive) { setData(null); setMsg('Could not load Sonia’s scoreboard.'); } })
            .finally(() => { if (alive) setLoading(false); });
        return () => { alive = false; };
    }, [audience, period]);

    if (loading) return <div style={{ ...card, color: MUTED, fontSize: 13 }}>Loading Sonia’s scoreboard…</div>;
    if (!data) return <div style={{ ...card, color: MUTED, fontSize: 13 }}>{msg}</div>;

    const c = data.review || {};
    const v = data.verdict || {};
    const needed = data.needed || {};
    const have = !!v.haveResults;

    return (
        <div>
            <h2 style={h2}>
                Sonia’s scoreboard — {data.periodLabel || data.period}
            </h2>

            {Array.isArray(data.periods) && data.periods.length > 1 && (
                <div style={{ marginBottom: 10 }}>
                    <select
                        value={period || data.period}
                        onChange={e => setPeriod(e.target.value)}
                        style={{ fontSize: 13, padding: '4px 8px', border: `1px solid ${LINE}`, borderRadius: 4, color: INK }}
                    >
                        {data.periods.map((p: string) => <option key={p} value={p}>{p}</option>)}
                    </select>
                </div>
            )}

            <div style={{ ...grid, marginBottom: 14 }}>
                <Cell label="Records worked" value={num(c.records)} note={`${num(c.notYetReached)} not reached yet`} />
                <Cell label="Messages sent" value={num(c.messagesSent)} note={c.spendAud != null ? `${money(c.spendAud)} spent` : undefined} />
                <Cell label="Replies" value={num(c.replied)} note={`${c.replyPct ?? 0}% of those contacted`} />
                <Cell label="Appointments booked" value={num(c.booked)} note={`${c.bookedPct ?? 0}% of those who replied`} />
                <Cell label="Opted out" value={num(c.optouts)} note={`${c.optoutPct ?? 0}% of those contacted`} />
                <Cell label="Hours given back" value={`${c.hoursBack ?? 0}`} note="at 1.5 min a message" />
            </div>

            <h2 style={h2}>What it returned</h2>

            {!have && (
                <div style={{ fontSize: 12.5, color: MUTED, marginBottom: 10 }}>
                    Waiting on three numbers only {audience === 'brokers' ? 'the broker' : 'you'} can give:
                    appointments attended, loans written and settled lending. Until they are in, the money
                    below is left blank rather than estimated.
                </div>
            )}

            <div style={grid}>
                {/* Richard, 14 Oct 2026: no Sonia fee on Fintrack's own clients - the money there is
                    $30,000 when a property sells. The fee, and the figures worked off it, are brokers only. */}
                {audience === 'brokers'
                    ? <Cell label="Paid" value={money(v.paidAud)} note="a month" />
                    : <Cell label="Per property sold" value={money(30000)} note="commission" />}
                <Cell label="Appointments attended" value={needed.attended != null ? num(needed.attended) : '—'} dim={needed.attended == null} note={needed.attended == null ? 'not entered' : undefined} />
                <Cell label="Loans written" value={needed.loans != null ? num(needed.loans) : '—'} dim={needed.loans == null} note={needed.loans == null ? 'not entered' : undefined} />
                <Cell label="Settled lending" value={have ? money(v.settledAud) : '—'} dim={!have} note={have ? undefined : 'not entered'} />
                <Cell label="Upfront" value={have ? money(v.upfrontAud) : '—'} dim={!have} />
                <Cell label="Trail added" value={have ? money(v.trailAddedAud) : '—'} dim={!have} note={have ? `${money(v.trailRunningAud)} running` : undefined} />
                {audience === 'brokers' && <Cell label="Return" value={v.returnX != null ? `${v.returnX}x` : '—'} dim={v.returnX == null} note="upfront against the fee" />}
                {audience === 'brokers' && <Cell label="Cost per appointment" value={v.costPerAppointment != null ? money(v.costPerAppointment) : '—'} dim={v.costPerAppointment == null} />}
            </div>

            {Array.isArray(data.roadblocks) && data.roadblocks.length > 0 && (
                <div style={{ ...card, marginTop: 14 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: MUTED, textTransform: 'uppercase', letterSpacing: '.08em', marginBottom: 6 }}>
                        What is in the way
                    </div>
                    {data.roadblocks.map((r: any, i: number) => (
                        <div key={i} style={{ fontSize: 13, color: INK, padding: '4px 0', borderTop: i ? `1px solid ${LINE}` : undefined }}>
                            {r.title || r.what || String(r)}
                            {r.detail && <span style={{ color: MUTED }}> — {r.detail}</span>}
                        </div>
                    ))}
                </div>
            )}

            <h2 style={{ ...h2, marginTop: 20 }}>What we agreed</h2>
            <div style={card}>
                {actions.length === 0 && (
                    <div style={{ fontSize: 12.5, color: MUTED }}>
                        Nothing agreed yet. Ask Sonia what to do about this month and write the answer down here.
                    </div>
                )}
                {actions.map((a: any, i: number) => (
                    <div key={a.id ?? i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '7px 0',
                                                  borderTop: i ? `1px solid ${LINE}` : undefined }}>
                        <input type="checkbox" checked={false} disabled={busy}
                               title="Tick it off" onChange={() => tick(a.id)} style={{ marginTop: 3 }} />
                        <div style={{ flex: 1, fontSize: 13, color: INK }}>
                            {a.title}
                            {a.detail && <div style={{ fontSize: 12, color: MUTED, marginTop: 2 }}>{a.detail}</div>}
                            <div style={{ fontSize: 11, color: MUTED, marginTop: 2 }}>
                                {a.owner || 'You'}{a.due ? ` · ${a.due}` : ''}
                            </div>
                        </div>
                    </div>
                ))}

                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                    <select value={owner} onChange={e => setOwner(e.target.value)}
                            style={{ fontSize: 13, padding: '6px 8px', border: `1px solid ${LINE}`, borderRadius: 4, color: INK }}>
                        <option value="You">You</option>
                        <option value="Sonia">Sonia</option>
                        <option value="Richard">Richard</option>
                    </select>
                    <input value={title} onChange={e => setTitle(e.target.value)}
                           onKeyDown={e => { if (e.key === 'Enter') add(); }}
                           placeholder="What are we doing about it?"
                           style={{ flex: 1, minWidth: 220, fontSize: 13, padding: '6px 9px', border: `1px solid ${LINE}`, borderRadius: 4, color: INK }} />
                    <input value={due} onChange={e => setDue(e.target.value)}
                           onKeyDown={e => { if (e.key === 'Enter') add(); }}
                           placeholder="By when"
                           style={{ width: 120, fontSize: 13, padding: '6px 9px', border: `1px solid ${LINE}`, borderRadius: 4, color: INK }} />
                    <button onClick={add} disabled={busy || !title.trim()}
                            style={{ fontSize: 13, padding: '6px 14px', borderRadius: 4, border: 'none',
                                     background: title.trim() ? INK : '#AAB1BD', color: '#fff',
                                     cursor: title.trim() ? 'pointer' : 'default' }}>
                        Add
                    </button>
                </div>
            </div>

            <h2 style={{ ...h2, marginTop: 20 }}>Ask Sonia</h2>
            <div style={card}>
                <div style={{ fontSize: 12.5, color: MUTED, marginBottom: 10 }}>
                    She is looking at this page. Every figure she gives comes off it — she will say
                    so rather than guess when the page does not answer you.
                </div>

                {chat.map((m, i) => (
                    <div key={i} style={{ fontSize: 13, color: m.role === 'user' ? MUTED : INK, padding: '5px 0',
                                          borderTop: i ? `1px solid ${LINE}` : undefined, whiteSpace: 'pre-wrap' }}>
                        <b style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '.07em', color: MUTED }}>
                            {m.role === 'user' ? 'You' : 'Sonia'}
                        </b>
                        <div>{m.content}</div>
                    </div>
                ))}

                <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                    <input value={question} onChange={e => setQuestion(e.target.value)}
                           onKeyDown={e => { if (e.key === 'Enter') ask(); }}
                           placeholder="What moved this month, and what would you do about it?"
                           style={{ flex: 1, fontSize: 13, padding: '7px 9px', border: `1px solid ${LINE}`, borderRadius: 4, color: INK }} />
                    <button onClick={ask} disabled={busy || !question.trim()}
                            style={{ fontSize: 13, padding: '7px 16px', borderRadius: 4, border: 'none',
                                     background: question.trim() ? INK : '#AAB1BD', color: '#fff',
                                     cursor: question.trim() ? 'pointer' : 'default' }}>
                        {busy ? '…' : 'Ask'}
                    </button>
                </div>
                {note && <div style={{ fontSize: 12, color: MUTED, marginTop: 8 }}>{note}</div>}
            </div>
        </div>
    );
};

export default Scoreboard;
